import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AccountsSubNav from '../components/AccountsSubNav';
import { appToast } from '../../../../components/AppToast';
import {
  AccountDetailsCard,
  BankDetailsCard,
  ContactInfoCard,
  TaxationCard,
  OrgStructureCheckboxPanel
} from '../../../../components/shared-details';

const STORAGE_KEY = 'bridge_erp_customers';

export default function CustomerEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: '',
    group: 'Sundry Debtors',
    alias: '',
    place: '',
    cost_center: 0,
    budget: 0,
    amount: 0,
    balance_type: 'Dr',
    hsn_sac: '',
    agent: '',
    remarks: '',
    companies: [],
    branches: [],
    departments: [],
    status: 'Active',
    // Contact Info
    custGroup: 'Select',
    contactPerson: '',
    designation: '',
    emailId: '',
    ccEmailId: '',
    billTo: '',
    shipTo: '',
    mobile: '',
    phone: '',
    contactInfo: '',
    creditDays: 0,
    interestRate: 0,
    interestPeriod: 'Year',
    birthDate: '',
    anniversary: '',
    // Bank Details
    bank: '',
    bankBranch: '',
    acNo: '',
    micrCo: '',
    ifscCode: '',
    bankType: 'Current',
    minPdc: 0,
    // Taxation
    taxType: 'Regular',
    taxState: 'Maharashtra',
    transporter: 'Select',
    pan: '',
    tds: 'Select',
    tdsCode: 'Select',
    aadhaarNo: '',
    tcs: '0%',
    msmeType: 'Select',
    taxCategory: 'Select',
    gta: 0,
    gst: '',
    pincode: '',
    distance: 0,
    tan: '',
    tdsRate: 0,
    resident: 'Select',
    cinNo: '',
    msmeNo: '',
    currency: 'INR',
    ledger: 'None'
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const list = JSON.parse(saved);
          const found = list.find(v => String(v.id) === String(id));
          if (found) {
            setForm(found);
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [id, isEdit]);

  const patchForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = (options = { exitAfter: true }) => {
    if (!form.name.trim()) {
      appToast.error('Customer Name is required.');
      return;
    }

    setIsSaving(true);

    try {
      const saved = localStorage.getItem(STORAGE_KEY) || '[]';
      let list = JSON.parse(saved);

      if (isEdit) {
        list = list.map(v => String(v.id) === String(id) ? { ...form } : v);
        appToast.success('Customer updated successfully!');
      } else {
        const newCust = {
          ...form,
          id: String(Date.now()),
          created_at: new Date().toISOString()
        };
        list = [newCust, ...list];
        appToast.success('Customer created successfully!');
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));

      if (options.exitAfter) {
        setTimeout(() => {
          navigate('/masters/acheads/customers/list');
        }, 350);
      } else {
        setForm({
          ...form,
          name: '',
          alias: '',
          place: '',
          mobile: '',
          emailId: '',
          gst: ''
        });
      }
    } catch (e) {
      appToast.error('Failed to save Customer.');
    } finally {
      setIsSaving(false);
    }
  };

  // Component Values
  const accountDetailsValue = {
    acGroup: form.group,
    acName: form.name,
    alias: form.alias,
    place: form.place,
    costCenter: Boolean(form.cost_center),
    budget: form.budget,
    hsnSac: form.hsn_sac,
    agent: form.agent,
    remarks: form.remarks
  };

  const handleAccountDetailsChange = (vals) => {
    patchForm('group', vals.acGroup || '');
    patchForm('name', vals.acName || '');
    patchForm('alias', vals.alias || '');
    patchForm('place', vals.place || '');
    patchForm('cost_center', vals.costCenter ? 1 : 0);
    patchForm('budget', Number(vals.budget || 0));
    patchForm('hsn_sac', vals.hsnSac || '');
    patchForm('agent', vals.agent || '');
    patchForm('remarks', vals.remarks || '');
  };

  const bankDetailsValue = {
    bank: form.bank,
    branch: form.bankBranch,
    acNo: form.acNo,
    micrCo: form.micrCo,
    ifscCode: form.ifscCode,
    type: form.bankType,
    minPdc: form.minPdc
  };

  const handleBankDetailsChange = (vals) => {
    patchForm('bank', vals.bank || '');
    patchForm('bankBranch', vals.branch || '');
    patchForm('acNo', vals.acNo || '');
    patchForm('micrCo', vals.micrCo || '');
    patchForm('ifscCode', vals.ifscCode || '');
    patchForm('bankType', vals.type || 'Current');
    patchForm('minPdc', Number(vals.minPdc || 0));
  };

  const contactInfoValue = {
    custGroup: form.custGroup,
    person: form.contactPerson,
    designation: form.designation,
    emailId: form.emailId,
    ccEmailId: form.ccEmailId,
    billTo: form.billTo,
    shipTo: form.shipTo,
    mobile: form.mobile,
    phone: form.phone,
    contactInfo: form.contactInfo,
    creditDays: form.creditDays,
    interestRate: form.interestRate,
    interestPeriod: form.interestPeriod,
    birthDate: form.birthDate,
    anniversary: form.anniversary
  };

  const handleContactInfoChange = (vals) => {
    patchForm('custGroup', vals.custGroup || 'Select');
    patchForm('contactPerson', vals.person || '');
    patchForm('designation', vals.designation || '');
    patchForm('emailId', vals.emailId || '');
    patchForm('ccEmailId', vals.ccEmailId || '');
    patchForm('billTo', vals.billTo || '');
    patchForm('shipTo', vals.shipTo || '');
    patchForm('mobile', vals.mobile || '');
    patchForm('phone', vals.phone || '');
    patchForm('contactInfo', vals.contactInfo || '');
    patchForm('creditDays', Number(vals.creditDays || 0));
    patchForm('interestRate', Number(vals.interestRate || 0));
    patchForm('interestPeriod', vals.interestPeriod || 'Year');
    patchForm('birthDate', vals.birthDate || '');
    patchForm('anniversary', vals.anniversary || '');
  };

  const taxationValue = {
    type: form.taxType,
    state: form.taxState,
    transporter: form.transporter,
    pan: form.pan,
    tds: form.tds,
    tdsCode: form.tdsCode,
    aadhaarNo: form.aadhaarNo,
    tcs: form.tcs,
    msmeType: form.msmeType,
    category: form.taxCategory,
    gta: Boolean(form.gta),
    gst: form.gst,
    pincode: form.pincode,
    distance: form.distance,
    tan: form.tan,
    tdsRate: form.tdsRate,
    resident: form.resident,
    cinNo: form.cinNo,
    msmeNo: form.msmeNo,
    currency: form.currency,
    ledger: form.ledger
  };

  const handleTaxationChange = (vals) => {
    patchForm('taxType', vals.type || 'Regular');
    patchForm('taxState', vals.state || 'Maharashtra');
    patchForm('transporter', vals.transporter || 'Select');
    patchForm('pan', vals.pan || '');
    patchForm('tds', vals.tds || 'Select');
    patchForm('tdsCode', vals.tdsCode || 'Select');
    patchForm('aadhaarNo', vals.aadhaarNo || '');
    patchForm('tcs', vals.tcs || '0%');
    patchForm('msmeType', vals.msmeType || 'Select');
    patchForm('taxCategory', vals.category || 'Select');
    patchForm('gta', vals.gta ? 1 : 0);
    patchForm('gst', vals.gst || '');
    patchForm('pincode', vals.pincode || '');
    patchForm('distance', Number(vals.distance || 0));
    patchForm('tan', vals.tan || '');
    patchForm('tdsRate', Number(vals.tdsRate || 0));
    patchForm('resident', vals.resident || 'Select');
    patchForm('cinNo', vals.cinNo || '');
    patchForm('msmeNo', vals.msmeNo || '');
    patchForm('currency', vals.currency || 'INR');
    patchForm('ledger', vals.ledger || 'None');
  };

  const groupOptions = [
    { value: 'Sundry Debtors', label: 'Sundry Debtors' }
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Customer' : 'Create Customer'}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <AccountsSubNav />
          <Link
            to="/masters/acheads/customers/list"
            className="inline-flex items-center gap-1 text-xs font-bold text-on-surface-variant hover:text-primary transition"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back</span>
          </Link>
        </div>
      </section>

      {/* Two-Column Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left Column */}
        <div className="flex flex-col gap-6">
          <AccountDetailsCard
            value={accountDetailsValue}
            onChange={handleAccountDetailsChange}
            groupOptions={groupOptions}
            onAddPlaceClick={() => appToast.info('Place master integration is under construction.')}
          />
          <BankDetailsCard
            value={bankDetailsValue}
            onChange={handleBankDetailsChange}
          />
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          <ContactInfoCard
            value={contactInfoValue}
            onChange={handleContactInfoChange}
          />
          <TaxationCard
            value={taxationValue}
            onChange={handleTaxationChange}
            onGstVerifyClick={() => appToast.info('Verifying GST details with GSTN portal...')}
          />
        </div>
      </div>

      {/* Full Width Bottom Organization Panels */}
      <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
        <div className="border-b border-outline-variant/20 pb-4 mb-6">
          <h2 className="font-display text-xl font-bold text-on-surface">Organization Scope</h2>
          <p className="text-xs text-on-surface-variant">
            Assign the Companies, Branches, and Departments where this Customer is authorized.
          </p>
        </div>
        <OrgStructureCheckboxPanel
          selectedCompanies={form.companies}
          selectedBranches={form.branches}
          selectedDepartments={form.departments}
          onCompaniesChange={(val) => patchForm('companies', val)}
          onBranchesChange={(val) => patchForm('branches', val)}
          onDepartmentsChange={(val) => patchForm('departments', val)}
        />
      </section>

      {/* Footer Actions Bar */}
      <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-4 shadow-sm">
        <button
          type="button"
          onClick={() => appToast.info('Audit history tracking is enabled.')}
          className="inline-flex items-center gap-1.5 rounded-xl bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant transition hover:bg-surface-container-high"
        >
          <span className="material-symbols-outlined text-sm">history</span>
          <span>History</span>
        </button>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/masters/acheads/customers/list')}
            className="rounded-xl bg-secondary-container px-6 py-2.5 text-xs font-bold text-on-secondary-container transition hover:brightness-95 cursor-pointer"
          >
            Cancel
          </button>

          {!isEdit && (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => handleSave({ exitAfter: false })}
              className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-6 py-2.5 text-xs font-bold text-primary transition hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
            >
              Save &amp; Continue
            </button>
          )}

          <button
            type="button"
            disabled={isSaving}
            onClick={() => handleSave({ exitAfter: true })}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-container px-8 py-2.5 text-xs font-bold text-on-primary shadow-lg shadow-primary/20 transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">save</span>
            <span>{isSaving ? 'Saving...' : 'Save & Exit'}</span>
          </button>
        </div>
      </section>
    </div>
  );
}
